import { createContext, useCallback, useContext, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { useI18n } from "./i18n-context";
import { Presence, presenceStateProps, usePresenceState } from "../components/ui/presence";

type CompletionKind = "task" | "goal";

type CompletionConfirmation = {
  kind: CompletionKind;
  title: string;
  onUndo?: () => void;
};

type CompletionConfirmationContextValue = {
  showCompletion: (kind: CompletionKind, title: string, onUndo?: () => void) => void;
};

const CompletionConfirmationContext = createContext<CompletionConfirmationContextValue | null>(null);

export function CompletionConfirmationProvider({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const [completion, setCompletion] = useState<CompletionConfirmation | null>(null);
  const [dismissing, setDismissing] = useState(false);
  const [undoProgress, setUndoProgress] = useState(1);
  const close = useCallback(() => setDismissing(true), []);
  const showCompletion = useCallback((kind: CompletionKind, title: string, onUndo?: () => void) => {
    setCompletion({ kind, title, onUndo });
    setDismissing(false);
    setUndoProgress(1);
  }, []);
  const undo = useCallback(() => {
    completion?.onUndo?.();
    close();
  }, [close, completion]);

  useEffect(() => {
    if (!completion || dismissing) {
      setUndoProgress(1);
      return;
    }
    const startedAt = Date.now();
    const timeout = window.setTimeout(close, 7000);
    const progressTimer = window.setInterval(() => {
      setUndoProgress(Math.max(0, 1 - (Date.now() - startedAt) / 7000));
    }, 100);
    return () => {
      window.clearTimeout(timeout);
      window.clearInterval(progressTimer);
    };
  }, [close, completion, dismissing]);

  const value = useMemo(() => ({ showCompletion }), [showCompletion]);

  return (
    <CompletionConfirmationContext.Provider value={value}>
      {children}
      <Presence
        open={completion !== null && !dismissing}
        exitDurationMs={220}
        onExited={() => {
          setCompletion(null);
          setDismissing(false);
        }}
      >
        {completion && !dismissing ? (
          <CompletionToastSurface completion={completion} undoProgress={undoProgress} onUndo={undo} onClose={close} t={t} />
        ) : null}
      </Presence>
    </CompletionConfirmationContext.Provider>
  );
}

function CompletionToastSurface({
  completion,
  undoProgress,
  onUndo,
  onClose,
  t,
}: {
  completion: CompletionConfirmation;
  undoProgress: number;
  onUndo: () => void;
  onClose: () => void;
  t: ReturnType<typeof useI18n>["t"];
}) {
  const presenceState = usePresenceState();
  return (
    <div className="plan-undo-toast completion-toast" role="status" aria-live="polite" {...presenceStateProps(presenceState)} style={{ "--undo-progress": undoProgress } as CSSProperties}>
      <div className="plan-undo-toast__inner">
        <span className="plan-undo-toast__text">{t(`completion.${completion.kind}Message`, { title: completion.title })}</span>
        <div className="plan-undo-toast__actions">
          {completion.onUndo ? (
            <button type="button" className="btn btn--ghost btn--sm plan-undo-toast__action" onClick={onUndo}>
              {t("plan.undo")}
            </button>
          ) : null}
          <button type="button" className="plan-undo-toast__close" onClick={onClose} aria-label={t("plan.closeUndo")} title={t("plan.closeUndo")}>
            ×
          </button>
        </div>
      </div>
    </div>
  );
}

export function useCompletionConfirmation() {
  const context = useContext(CompletionConfirmationContext);
  if (!context) throw new Error("useCompletionConfirmation must be used within CompletionConfirmationProvider");
  return context;
}
